import type React from 'react';
import { useEffect, useState } from 'react';
import { Form, Radio, Row, Col, InputNumber } from 'antd';
import FormInput from 'components/FormInput';
import GetLabel from 'DisplayFramework/components/GetLabel';
import ButtonLink from 'components/theme_components/ButtonLink';
import InputCurrency from 'components/InputCurrency';
import InputRate from 'components/InputRate';
import StatCard from 'components/theme_components/StatCard';

type ProductAmortissementModalProps = {
  setVisible: (visible: boolean) => void;
  mode: string;
  data: any;
  initamount?: number;
  onAddCard?: (newCard: any) => void;
  onUpdateCard?: (updatedCard: any) => void;
  durationmax?: number;
  defaultStartDay?: number;
  defaultEndDay?: number;
  finpercent?: number;
};

const ProductAmortissementModal: React.FC<ProductAmortissementModalProps> = ({
  setVisible,
  mode,
  data,
  initamount,
  onAddCard,
  onUpdateCard,
  durationmax,
  defaultStartDay,
  defaultEndDay,
  finpercent = 100, // Default to 100 if not provided
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    form.resetFields();
    if (mode === 'create') {
      form.setFieldsValue({
        amtstartday: defaultStartDay ?? 1,
        amtendday: defaultEndDay ?? durationmax,
      });
    } else if ((mode === 'update' || mode === 'edit') && data) {
      form.setFieldsValue({ ...data });
    }
  }, [mode, data, form, durationmax, defaultStartDay, defaultEndDay]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const card = {
        key: mode === 'update' ? data.key : data.length + 1,
        periodnumber: mode === 'create' ? data.length + 1 : data.periodnumber,
        amtstartday: values.amtstartday,
        amtendday: values.amtendday,
        cteperiodicity: values.cteperiodicity,
        amortbillingday: values.amortbillingday,
        cteamortizedamount: values.cteamortizedamount,
        cteoutstandingamount: values.cteoutstandingamount,
        cteamortizedcoef: values.cteamortizedcoef,
        cteoutstandingcoef: values.cteoutstandingcoef,
        prefinsteptype: 'CAP',
      };
      if (mode === 'create') {
        onAddCard && onAddCard(card);
      } else if (mode === 'edit' || mode === 'update') {
        onUpdateCard && onUpdateCard({ ...card, id: data.id });
      }
      setVisible(false);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      className="no-border-radius ut-wizard-form"
    >
      {/* Row: Jours de calcul */}
      <Row gutter={16}>
        <Col span={24} className="mb-6 ">
          <GetLabel
            label="dayscalculated"
            className="font-weight-500 font-size-15"
          />
        </Col>
        <Col span={12}>
          <FormInput
            name="amtstartday"
            rules={[
              { required: true, message: 'Veuillez saisir un jour de début' },
            ]}
          >
            <InputNumber
              min={1}
              className="no-border-radius ut-wizard-form color-gray"
              disabled // Make it read-only
            />
          </FormInput>
        </Col>
        <Col span={12}>
          <FormInput
            name="amtendday"
            rules={[
              { required: true, message: 'Veuillez saisir un jour de fin' },
            ]}
          >
            <InputNumber
              min={1}
              max={durationmax}
              className="no-border-radius ut-wizard-form color-gray"
              onChange={() => form.validateFields(['cteamortizedcoef'])}
            />
          </FormInput>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={24} className="mb-6 ">
          <GetLabel
            label="periodicityCondition"
            className="font-weight-500 font-size-15"
          />
        </Col>
        <Col span={12}>
          <FormInput
            name="cteperiodicity"
            rules={[
              {
                required: true,
                message: 'Veuillez saisir Périodicité (jours)',
              },
            ]}
          >
            <InputNumber
              min={1}
              className="no-border-radius ut-wizard-form color-gray"
              onChange={() => form.validateFields(['cteamortizedcoef'])}
            />
          </FormInput>
        </Col>
        <Col span={12}>
          <FormInput
            name="amortbillingday"
            rules={[
              {
                required: true,
                message: 'Veuillez saisir un Jour de facturation',
              },
            ]}
          >
            <InputNumber
              min={1}
              className="no-border-radius ut-wizard-form color-gray"
            />
          </FormInput>
        </Col>

        <Col span={12}>
          <FormInput
            name="cteamortizedcoef"
            rules={[
              { required: true, message: 'Veuillez saisir le taux' },
              {
                validator: (_, value) => {
                  const amtstartday = form.getFieldValue('amtstartday');
                  const amtendday = form.getFieldValue('amtendday');
                  const cteperiodicity = form.getFieldValue('cteperiodicity');
                  if (value < 0 || value > 100) {
                    return Promise.reject(
                      new Error('Le taux doit être entre 0 et 100')
                    );
                  }
                  if (!amtstartday || !amtendday || !cteperiodicity) {
                    return Promise.resolve();
                  }

                  const currentPercent =
                    value * ((amtendday - amtstartday + 1) / cteperiodicity);

                  if (currentPercent > finpercent) {
                    return Promise.reject(
                      new Error(
                        `Le pourcentage calculé (${currentPercent}%) ne doit pas dépasser ${finpercent}%`
                      )
                    );
                  }

                  // Sum all previous calculated percentages except current
                  const currentKey = data?.key;
                  const otherPercents = Array.isArray(data)
                    ? data.reduce((acc: number, item: any) => {
                        if (item.key !== currentKey) {
                          const percent =
                            (Number(item.cteamortizedcoef) || 0) *
                            ((item.amtendday - item.amtstartday + 1) /
                              item.cteperiodicity);
                          return acc + percent;
                        }
                        return acc;
                      }, 0)
                    : 0;

                  const totalPercent = otherPercents + currentPercent;

                  if (totalPercent > finpercent) {
                    return Promise.reject(
                      new Error(
                        `Le total des pourcentages (${totalPercent}%) ne doit pas dépasser ${finpercent}%`
                      )
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputRate
              min={0}
              className="no-border-radius ut-wizard-form border-color-gray"
            />
          </FormInput>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item shouldUpdate noStyle>
            {() => {
              const amtstartday = form.getFieldValue('amtstartday');
              const amtendday = form.getFieldValue('amtendday');
              const cteperiodicity = form.getFieldValue('cteperiodicity');
              const cteamortizedcoef = form.getFieldValue('cteamortizedcoef');
              let currentPercent = 0;
              if (
                amtstartday &&
                amtendday &&
                cteperiodicity &&
                cteamortizedcoef !== undefined
              ) {
                currentPercent =
                  Number(cteamortizedcoef) *
                  ((amtendday - amtstartday + 1) / cteperiodicity);
              }
              const finpercent = form.getFieldValue('finpercent') || 100;
              // Sum all previous calculated percentages except current
              const currentKey = data?.key;
              const otherPercents = Array.isArray(data)
                ? data.reduce((acc: number, item: any) => {
                    if (item.key !== currentKey) {
                      const percent =
                        (Number(item.cteamortizedcoef) || 0) *
                        ((item.amtendday - item.amtstartday + 1) /
                          item.cteperiodicity);
                      return acc + percent;
                    }
                    return acc;
                  }, 0)
                : 0;

              const totalPercent = finpercent - otherPercents;

              return (
                <StatCard
                  title="Taux maximum autorisé"
                  value={`${totalPercent}%`}
                  variant='secondary'
                  valueStyle={{
                    color: '#209e70',
                    fontWeight: 600,
                    fontSize: '1rem',
                  }}
                  className="mb-3"
                  height="5rem"
                />
              );
            }}
          </Form.Item>
        </Col>
      </Row>

      <Row justify={'end'} className="mt-7">
        <ButtonLink
          comcode="Annuler"
          className="ut-btn-bordred"
          onClick={() => setVisible(false)}
        />
        <ButtonLink
          type="primary"
          comcode="validate"
          htmlType="submit"
          className="ut-reset-btn"
          onClick={handleSubmit}
          iconPosition="end"
        />
      </Row>
    </Form>
  );
};

export default ProductAmortissementModal;
